/**
 * Custom Embroidery Cart Sync Pipeline - Stable Variant ID Edition
 * Ties parent product items to their corresponding embroidery addon charges
 */
(function() {
  const isCartInterfacePresent = document.querySelector('form[action="/cart"], cart-items, #cart-drawer, .cart-drawer, .cart-items, [data-cart-container]');
  if (!isCartInterfacePresent) return;

  document.addEventListener('DOMContentLoaded', function() {
    const DEBUG = true;
    let isSyncing = false;

    function log(message, data) {
      if (DEBUG) console.log('[Cart Sync]', message, data || '');
    }

    function updateCartQuantityDisplays(totalItems) {
      document.querySelectorAll('.cart-count-bubble').forEach(bubble => {
        const countElement = bubble.querySelector('span:not(.visually-hidden), bubble span[aria-hidden="true"]');
        if (countElement) countElement.textContent = totalItems;
      });

      const mobileCartCount = document.querySelector('.mobile-cart-count');
      if (mobileCartCount) mobileCartCount.textContent = totalItems;
    }

    async function getCartState() {
      return await fetch('/cart.js').then(r => r.json());
    }

    /**
     * Core Sync Routine
     * Uses static _parent_variant_id instead of volatile line keys
     */
    async function syncAllEmbroideryItems() {
      if (isSyncing) return;
      isSyncing = true;
      
      try {
        log('Executing background alignment sequence...');
        const cart = await getCartState();
        
        const embroideryItems = cart.items.filter(item => item.properties?._is_embroidery_addon === "true");
        const parentItems = cart.items.filter(item => !item.properties?._is_embroidery_addon);

        const updates = {};
        let requiresBackendSync = false;

        // Keep track of matched parents so multiple items with the same variant map 1:1
        const matchedParentKeys = new Set();

        embroideryItems.forEach(embItem => {
          const parentVariantId = String(embItem.properties?._parent_variant_id || '');
          
          // Match by variant ID instead of line key
          const parentItem = parentItems.find(p => {
            const matchesVariant = String(p.variant_id) === parentVariantId;
            return matchesVariant && !matchedParentKeys.has(p.key);
          });
          
          if (!parentItem) {
            // Parent is genuinely deleted from cart
            updates[embItem.key] = 0;
            requiresBackendSync = true;
          } else {
            matchedParentKeys.add(parentItem.key);
            if (embItem.quantity !== parentItem.quantity) {
              updates[embItem.key] = parentItem.quantity;
              requiresBackendSync = true;
            }
          }
        });

        if (requiresBackendSync) {
          log('Correcting post-mutation state drift:', updates);
          const updateResponse = await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates: updates })
          });
          const updatedCart = await updateResponse.json();
          updateCartQuantityDisplays(updatedCart.item_count);
        }
        
        await refreshCartUI();
      } catch (error) {
        console.error('State alignment failure:', error);
      } finally {
        isSyncing = false;
      }
    }

    async function refreshCartUI() {
      try {
        const cart = await getCartState();
        
        if (cart.item_count === 0) {
          log('Cart is empty. Handing control to native theme element engines.');
          return;
        }

        cart.items.forEach(item => {
          const itemRow = document.querySelector(`tr[data-line-key="${item.key}"]`) || document.querySelector(`[data-line-key="${item.key}"]`);
          if (!itemRow) return;

          const qtyInput = itemRow.querySelector('.quantity__input');
          if (qtyInput) {
            qtyInput.value = item.quantity;
          }
          
          const textualContainer = itemRow.querySelector('.cart-item__qty-text, .qty-display');
          if (textualContainer) textualContainer.textContent = `Qty: ${item.quantity}`;

          itemRow.querySelectorAll('[data-hulkapps-line-price], .line-item__price, .cart-item__line-price, .cart-item__price-wrapper').forEach(el => {
            const formatPrice = `₹${(item.final_line_price / 100).toFixed(2)}`;
            if (el.textContent.trim() !== formatPrice) el.textContent = formatPrice;
          });
        });
        
        document.querySelectorAll('[data-hulkapps-cart-total], .cart__totals-subtotal-value, .totals__subtotal-value').forEach(el => {
          const formatTotal = `₹${(cart.total_price / 100).toFixed(2)}`;
          if (el.textContent.trim() !== formatTotal) el.textContent = formatTotal;
        });
        
        updateCartQuantityDisplays(cart.item_count);
        lockEmbroideryRowsVisuals(cart);
      } catch (error) {
        console.error('UI Redraw pipeline failure:', error);
      }
    }

    function lockEmbroideryRowsVisuals(cart) {
      const embroideryItems = cart.items.filter(item => item.properties?._is_embroidery_addon === "true");

      embroideryItems.forEach(embItem => {
        const embRow = document.querySelector(`tr[data-line-key="${embItem.key}"]`) || document.querySelector(`[data-line-key="${embItem.key}"]`);
        if (!embRow) return;

        const embroideryQtyInput = embRow.querySelector('.quantity__input');
        if (embroideryQtyInput) {
          embroideryQtyInput.disabled = true;
          const wrapper = embroideryQtyInput.closest('.quantity-popover-wrapper') || embroideryQtyInput.closest('.quantity-input') || embroideryQtyInput.closest('.quantity');
          if (wrapper) {
            wrapper.classList.add('quantity--disabled');
            wrapper.querySelectorAll('button').forEach(b => b.style.display = 'none');
          }
        }
      });
    }

    function injectAtomicInterceptors() {
      const originalFetch = window.fetch;
      
      window.fetch = async function(...args) {
        const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
        const options = args[1] || {};

        const isCartMutationPath = url.includes('/cart/change') || url.includes('/cart/update') || url.includes('/cart/add');

        if (isCartMutationPath && options.body && !isSyncing) {
          try {
            let payload = {};
            
            if (typeof options.body === 'string') {
              payload = JSON.parse(options.body);
            } else if (options.body instanceof FormData) {
              payload = Object.fromEntries(options.body.entries());
            }

            // If this request is simply applying or removing a discount code, bypass quantity recalculation
            if (payload.discount !== undefined && !payload.updates && !payload.id && !payload.line) {
              return await originalFetch.apply(this, args);
            }

            const cart = await originalFetch('/cart.js').then(r => r.json());
            const embroideryItems = cart.items.filter(item => item.properties?._is_embroidery_addon === "true");
            
            let targetKey = payload.id || payload.line || null;
            let incomingQuantity = payload.quantity !== undefined ? parseInt(payload.quantity) : null;

            if (payload.updates) {
              for (const [key, qty] of Object.entries(payload.updates)) {
                const numericQty = parseInt(qty) || 0;
                const parentItem = cart.items.find(i => i.key === key);
                if (parentItem) {
                  const correlatedAddon = embroideryItems.find(item => String(item.properties?._parent_variant_id) === String(parentItem.variant_id));
                  if (correlatedAddon) {
                    payload.updates[correlatedAddon.key] = numericQty;
                  }
                }
              }
            } else if (targetKey && incomingQuantity !== null) {
              let matchedParentItem = null;
              
              if (!isNaN(targetKey) && String(targetKey).length < 5) {
                const index = parseInt(targetKey) - 1;
                matchedParentItem = cart.items[index];
              } else {
                matchedParentItem = cart.items.find(item => item.key === targetKey || item.id == targetKey);
              }

              if (matchedParentItem && !matchedParentItem.properties?._is_embroidery_addon) {
                const correlatedAddon = embroideryItems.find(item => String(item.properties?._parent_variant_id) === String(matchedParentItem.variant_id));
                
                if (correlatedAddon) {
                  if (payload.updates === undefined) {
                    payload.updates = {};
                  }

                  cart.items.forEach(item => {
                    if (item.key === matchedParentItem.key) {
                      payload.updates[item.key] = incomingQuantity;
                    } else if (item.key === correlatedAddon.key) {
                      payload.updates[item.key] = incomingQuantity;
                    } else {
                      payload.updates[item.key] = item.quantity;
                    }
                  });

                  delete payload.id;
                  delete payload.line;
                  delete payload.quantity;

                  if (typeof args[0] === 'string') {
                    args[0] = '/cart/update.js';
                  } else if (args[0]?.url) {
                    args[0].url = '/cart/update.js';
                  }
                  
                  options.headers = options.headers || {};
                  options.headers['Content-Type'] = 'application/json';
                  options.body = JSON.stringify(payload);
                }
              }
            }
          } catch (err) {
            console.error('Payload mutation error:', err);
          }
        }

        const response = await originalFetch.apply(this, args);

        if (isCartMutationPath && !isSyncing) {
          setTimeout(syncAllEmbroideryItems, 50);
        }

        return response;
      };
    }

    function setupEventListeners() {
      document.addEventListener('click', function(e) {
        const trashBtn = e.target.closest('cart-remove-button, .cart-item__remove, .cart-remove-btn, .remove-entry');
        if (trashBtn) {
          const itemRow = trashBtn.closest('tr.cart-item') || trashBtn.closest('[data-line-key]');
          const input = itemRow?.querySelector('.quantity__input');
          if (input) {
            input.value = 0;
            itemRow.style.display = 'none';
          }
          setTimeout(syncAllEmbroideryItems, 100);
        }
      });

      document.addEventListener('cart:updated', () => setTimeout(syncAllEmbroideryItems, 50));
      document.addEventListener('cart:requestComplete', () => setTimeout(syncAllEmbroideryItems, 50));
    }

    injectAtomicInterceptors();
    setupEventListeners();
    syncAllEmbroideryItems();
  });
})();